import { useEffect, useRef } from "react";

interface UseInfiniteScrollMessagesProps {
  /**
   * Whether there are more pages to load
   */
  hasNextPage: boolean | undefined;
  /**
   * Whether the next page is currently being fetched
   */
  isFetchingNextPage: boolean;
  /**
   * Function to fetch the next page
   */
  fetchNextPage: () => void;
  /**
   * Current number of pages loaded
   */
  pageCount: number;
  /**
   * Data that has been loaded (used to trigger scroll effects)
   */
  data: unknown;
  /**
   * Dependency that should trigger a reset of scroll state (e.g., conversation ID)
   */
  resetDependency?: string | number;
}

interface UseInfiniteScrollMessagesReturn {
  /**
   * Ref to attach to the load more sentinel element
   */
  loadMoreRef: React.RefObject<HTMLDivElement>;
  /**
   * Ref to attach to the scrollable container
   */
  containerRef: React.RefObject<HTMLDivElement>;
  /**
   * Function to scroll to the bottom of the container
   */
  scrollToBottom: () => void;
}

/**
 * Custom hook for handling infinite scroll of messages with proper scroll position management.
 *
 * Features:
 * - Scrolls to bottom on initial load
 * - Maintains scroll position when loading earlier messages
 * - Uses IntersectionObserver to trigger loading more messages
 * - Resets scroll state when resetDependency changes
 *
 * @example
 * const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useGetConversationMessages(...);
 * const { loadMoreRef, containerRef } = useInfiniteScrollMessages({
 *   hasNextPage,
 *   isFetchingNextPage,
 *   fetchNextPage,
 *   pageCount: data?.pages?.length ?? 0,
 *   data,
 *   resetDependency: conversationId,
 * });
 */
export const useInfiniteScrollMessages = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  pageCount,
  data,
  resetDependency,
}: UseInfiniteScrollMessagesProps): UseInfiniteScrollMessagesReturn => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousScrollHeight = useRef<number>(0);
  const isInitialLoad = useRef(true);
  const previousPageCount = useRef(0);

  const scrollToBottom = () => {
    const container = containerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
          });
        }
      });
    });
  };

  // Reset scroll state when resetDependency changes
  useEffect(() => {
    isInitialLoad.current = true;
    previousPageCount.current = 0;
  }, [resetDependency]);

  // Handle scroll position: scroll to bottom on initial load, maintain position when loading more
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !data) return;

    // Initial load: scroll to bottom
    if (isInitialLoad.current) {
      container.scrollTop = container.scrollHeight;
      isInitialLoad.current = false;
      previousScrollHeight.current = container.scrollHeight;
      previousPageCount.current = pageCount;
      return;
    }

    // Loading more messages: maintain scroll position
    if (pageCount > previousPageCount.current) {
      const scrollHeightDiff = container.scrollHeight - previousScrollHeight.current;
      container.scrollTop += scrollHeightDiff;
      previousScrollHeight.current = container.scrollHeight;
      previousPageCount.current = pageCount;
    }
  }, [data, pageCount]);

  // IntersectionObserver to trigger loading more messages
  useEffect(() => {
    const container = containerRef.current;
    const sentinel = loadMoreRef.current;

    if (!container || !sentinel || !hasNextPage || isFetchingNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          previousScrollHeight.current = container.scrollHeight;
          fetchNextPage();
        }
      },
      { threshold: 0.1, root: container }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    loadMoreRef,
    containerRef,
    scrollToBottom,
  };
};
